import moment from 'moment'
import {
    OnboardingApplicationResponse, GetOnboardingApplicationQuery,
    OnboardingApplicationPatchRequest, OnboardingApplicationTransferRequest,
    SgiIdHistoryModel, SgiIdAssignmentUpsertRequest, PagedEntityList
} from '@herbjoy/apex-api-models';
import { IHttpService, IPromise } from 'angular';

import { OnboardingApplication } from '../../../apex-main-app/onboarding/application/edit-onboarding-application/edit-onboarding-application.component';

export class OnboardingApplicationService {
    constructor(private $http: IHttpService, private apexApiUrl: string) {
        'ngInject';
    }

    retrieveOnboardingApplications(): IPromise<OnboardingApplicationResponse> {
        return this.$http<OnboardingApplicationResponse>({
            method: "GET",
            url: this.apexApiUrl + "onboarding-applications",
            headers: { Accept: "application/json" }
        }).then(response => {
            return response.data;
        });
    }

    retrieveOnboardingApplicationsPage(pageIndex: number, query: GetOnboardingApplicationQuery):
        IPromise<PagedEntityList<OnboardingApplicationResponse>> {
        return this.$http<PagedEntityList<OnboardingApplicationResponse>>({
            method: "GET",
            url: this.apexApiUrl + "onboarding-applications/page/" + (pageIndex || 0).toString(),
            params: query,
            headers: { Accept: "application/json" }
        })
            .then(response => {
                return response.data;
            });
    }

    retrieveOnboardingApplication(id: number): IPromise<OnboardingApplicationResponse> {
        return this.$http<OnboardingApplicationResponse>({
            method: "GET",
            url: this.apexApiUrl + "onboarding-applications/" + id,
            headers: { Accept: "application/json" }
        }).then(response => {
            return response.data;
        });
    }

    createOnboardingApplication(onboardingApplication: OnboardingApplication) {
        // The new onboarding application must be submitted as a FormData
        // since some of the fields have to be in multipart/form-data format.
        const formData = new FormData();
        formData.append("IsSelfSignUp", onboardingApplication.IsSelfSignUp || false);
        formData.append("DivisionID", onboardingApplication.DivisionID);
        formData.append("IcdOfficeID", onboardingApplication.IcdOfficeID);
        formData.append("Password", onboardingApplication.Password || "");
        formData.append("Applicant.FirstName", onboardingApplication.Applicant.FirstName || "");
        formData.append("Applicant.LastName", onboardingApplication.Applicant.LastName || "");
        formData.append("Applicant.EmailAddress", onboardingApplication.Applicant.EmailAddress || "");
        formData.append("Applicant.UserName", onboardingApplication.Applicant.UserName || "");
        if (onboardingApplication.Applicant.CellPhone) {
            formData.append("Applicant.CellPhone.PhoneNumber", onboardingApplication.Applicant.CellPhone.PhoneNumber || "");
            formData.append("Applicant.CellPhone.Extension", onboardingApplication.Applicant.CellPhone.Extension || "");
        }
        const dob = onboardingApplication.Applicant.DateOfBirth;
        formData.append("Applicant.DateOfBirth", dob ? moment(dob).format("YYYY-MM-DD") : "");
        onboardingApplication.OnboardingDocuments.forEach(function (onboardingDocument, i) {
            formData.append("OnboardingDocuments[" + i + "]", onboardingDocument);
        });
        formData.append("InitialOnboardingApplicationStatusID", onboardingApplication.InitialOnboardingApplicationStatusID);

        return this.$http({
            method: "POST",
            url: this.apexApiUrl + "onboarding-applications",
            data: formData,
            // The following two lines are required to submit the request as multipart/form-data.
            transformRequest: id => id,
            headers: { "Content-Type": undefined }
        }).then(response => {
            return response.data;
        });
    }

    patchOnboardingApplication(id: number, patch: OnboardingApplicationPatchRequest): IPromise<void> {
        return this.$http({
            method: "PATCH",
            url: this.apexApiUrl + "onboarding-applications/" + id,
            data: patch
        }).then(() => null);
    }

    deleteOnboardingApplication(id: number): IPromise<void> {
        return this.$http({
            method: "DELETE",
            url: this.apexApiUrl + "onboarding-applications/" + id
        }).then(() => null);
    }

    updateOnboardingDocument(fileUrl, document) {
        const formData = new FormData();
        formData.append("document", document);

        return this.$http({
            method: "PUT",
            url: fileUrl,
            data: formData,
            transformRequest: id => id,
            headers: { "Content-Type": undefined }
        });
    }

    transferOnboardingApplication(id: number, transfer: OnboardingApplicationTransferRequest): IPromise<void> {
        return this.$http({
            method: "PATCH",
            url: this.apexApiUrl + "onboarding-application-transfer/" + id,
            data: transfer
        }).then(() => null);
    }

    /**
     * Get the history of status changes for the given application.
     * @param {number} onboardingApplicationID
     * @returns {Promise<{OnboardingStatus: {Id: number, Value: string, Text: string}, EffectiveDate: Date, Comments: string}>}
     *     The resolved list of status changes.
     */
    //retrieveOnboardingApplicationStatusHistory(onboardingApplicationID) {
    //    const statuses = this.onboardingStatusService.retrieveOnboardingStatuses();
    //    const statusHistory = this.$http({
    //        method: "GET",
    //        url: this.apexApiUrl + "onboarding-applications/" + onboardingApplicationID + "/status-history",
    //        headers: { "Content-Type": "application/json" }
    //    }).then(response => {
    //        return response.data;
    //    });

    //    return this.$q.all([statuses, statusHistory]).then(v => {
    //        const statuses: any = v[0];
    //        const statusHistory: any = v[1];

    //        statusHistory.forEach(statusChange => {
    //            // Replace the status change enum value to an object with a Text and Id field.
    //            const value = statusChange.OnboardingStatus;
    //            statusChange.OnboardingStatus = statuses.filter(status => {
    //                return status.Value === value;
    //            })[0] ||
    //            {
    //                Id: value,
    //                Value: value,
    //                Text: value
    //            };
    //        });

    //        return statusHistory;
    //    });
    //}

    /**
     * Update the given onboarding application's reference field value.
     * @param {Number} onboardingApplicationID The application whose field is being updated.
     * @param {Number} index The index of the reference field in the list.
     * @param {String} value The new value to give to the application.
     * @returns {Promise} async
     */
    updateReferenceFieldValue(onboardingApplicationID: number, index: number, value: string): IPromise<void> {
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "onboarding-applications/" + onboardingApplicationID + "/fields/" + index,
            data: value
        }).then(() => null);
    }

    retrieveEmployeeSgiIdHistory(icdofficeID: number, employeeID: number): IPromise<SgiIdHistoryModel> {
        const params: any = {};
        params.icdofficeID = icdofficeID;
        params.employeeID = employeeID;
        return this.$http<SgiIdHistoryModel>({
            method: "GET",
            params: params,
            url: this.apexApiUrl + "employee-sgi-id-history",
            headers: { Accept: "application/json" }
        }).then(response => {
            return response.data;
        });
    }

    retrieveSgiIdReport(filters: any): IPromise<SgiIdHistoryModel> {
        return this.$http<SgiIdHistoryModel>({
            method: "GET",
            url: this.apexApiUrl + "/sgi-id-report",
            headers: {
                Accept: "application/json"
            },
            params: filters
        }).then(response => {
            return response.data;
        });
    }

    upsertEmployeeSgiIdHistory(data: SgiIdAssignmentUpsertRequest) {
        return this.$http({
            method: "POST",
            data: data,
            url: this.apexApiUrl + "employee-sgi-id-history",
            headers: { Accept: "application/json" }
        }).then(response => {
            return response.data;
        });
    }

    deleteEmployeeSgiIdHistory(id: number) {

        return this.$http({
            method: "DELETE",
            url: this.apexApiUrl + "employee-sgi-id-history/" + id

        }).then(response => {
            return response.data;
        });
    }

    /**
     * Create a new onboarding application with default properties set.
     */
    createOnboardingApplicationVM() {
        return {
            OnboardingDocuments: [],
            Applicant: {
                CellPhone: {}
            },
            IsSelfSignUp: null
        };
    }
}
